import { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import GoogleMap from "google-map-react";
import cn from "classnames";

import styles from "./styles/book.json";

export function Map({
  className = "",
  options = {},
  boundsPadding = null,
  children,
}) {
  const mapRef = useRef(null);

  /**
   * Store the handles returned from the Google Maps API
   */
  const gmap = useRef(null);
  const gmaps = useRef(null);

  /**
   * Set sensible defaults and allow top-level options object to override
   *
   * Docs aren't great, but they're here:
   * https://developers.google.com/maps/documentation/javascript/controls
   */
  const config = {
    mapTypeControl: false,
    fullscreenControl: false,
    streetViewControl: false,
    styles,
    ...options,
  };

  const resetBounds = () => {
    const bounds = new gmaps.current.LatLngBounds();

    /**
     * Collect `LatLng` defs from marker items and extend to a `LatLngBounds` def
     * so the map can "auto-zoom" while containing all markers
     */
    children.forEach(child => {
      bounds.extend(new gmaps.current.LatLng(child.props.lat, child.props.lng));
    });

    gmap.current.fitBounds(bounds, boundsPadding);
  };

  const handleApiLoaded = ({ map, maps }) => {
    gmap.current = map;
    gmaps.current = maps;

    resetBounds();

    /**
     * Refit the bounds to a resized component
     */
    maps.event.addDomListenerOnce(map, "idle", () => {
      maps.event.addDomListener(window, "resize", () => resetBounds());
    });
  };

  useEffect(() => {
    if (gmap.current) {
      // TODO: Does this make the call in `handleApiLoaded` redundant?
      resetBounds();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [children]);

  return (
    <div className={cn("w-full h-full bg-black-100", className)}>
      <GoogleMap
        ref={mapRef}
        center={{
          lat: children[0].props.lat,
          lng: children[0].props.lng,
        }}
        bootstrapURLKeys={{ key: process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY }}
        defaultZoom={12}
        options={config}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={handleApiLoaded}
      >
        {children}
      </GoogleMap>
    </div>
  );
}

Map.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
  className: PropTypes.string,
  options: PropTypes.shape({}),
  boundsPadding: PropTypes.oneOfType([
    PropTypes.shape({
      top: PropTypes.number,
      right: PropTypes.number,
      bottom: PropTypes.number,
      left: PropTypes.number,
    }),
    PropTypes.oneOf([null, false]),
  ]),
};
